import { add, endOfToday, formatDuration, isAfter, isBefore, startOfDay, sub } from 'date-fns';

import { UTC } from 'constants/Date';
import { ERROR_MATCHERS, ERROR_NOTIFICATION, FOREIGN_ENTITY_TYPES } from 'constants/InterventionConstants';
import {
  DOCUMENT_STATUS,
  SELF_SERVICE_TYPE,
  SELF_SERVICE_TYPES,
  SELF_SERVICE_STATUS,
  SELF_SERVICE_ORIGIN,
  SELF_SERVICE_ORIGINS,
  ALL_SELF_SERVICE_STATUS
} from 'constants/SelfServiceConstants';

import { isSelfServiceStatusBelow } from 'modules/interventions/utils/selfServiceStatus';
import { convertTimeZones, tryParse } from 'utils/date';
import { humanize } from 'utils/global';

import parseISODuration from '../polyfill/parseISODuration';

/**
 * @typedef {(import('modules/interventions/types/projections/kanban-intervention').KanbanIntervention | import('modules/interventions/types/projections/detailed-intervention').DetailedIntervention) & { dealer: unknown }} InterventionWithDealer
 */

/**
 * @param {Pick<import('modules/selfServices/types').SelfServiceData, 'originContext'>} selfService
 * @returns {{origin: import('modules/selfServices/types').SelfServiceOriginEnum} & import('modules/selfServices/types').SelfServiceStatusEnum>}
 */
const getSelfServiceContext = selfService => {
  const originContext = selfService?.originContext ?? {};
  const [context] = Object.entries(originContext)
    .flatMap(([origin, selfContext]) => ({ ...selfContext, origin }))
    .sort((a, b) => ALL_SELF_SERVICE_STATUS.indexOf(b.status) - ALL_SELF_SERVICE_STATUS.indexOf(a.status));
  return context;
};

/**
 * @param {Pick<import('modules/selfServices/types').SelfServiceData, 'originContext'>} selfService
 * @returns {import('modules/selfServices/types').SelfServiceStatusEnum}
 */
export const getSelfServiceStatus = selfService => getSelfServiceContext(selfService)?.status;

const REVERSED_SELF_SERVICE_ORIGINS = [...SELF_SERVICE_ORIGINS].reverse();

/** @typedef {{origin: import('modules/selfServices/types').SelfServiceOriginEnum, type: import('modules/selfServices/types').SelfServiceTypeEnum, status: import('modules/selfServices/types').SelfServiceStatusEnum}} Context */

/**
 * @param {Array<Context>} contexts
 * @returns {Array<Context>}
 */
const sortByTypeOriginAndStatus = contexts =>
  contexts.sort((a, b) => {
    // Sort by type
    if (a.type === SELF_SERVICE_TYPE.CHECK_OUT && b.type !== SELF_SERVICE_TYPE.CHECK_OUT) {
      return -1;
    }
    if (a.type !== SELF_SERVICE_TYPE.CHECK_OUT && b.type === SELF_SERVICE_TYPE.CHECK_OUT) {
      return 1;
    }

    // Sort by status
    const aStatusIndex = ALL_SELF_SERVICE_STATUS.indexOf(a.status);
    const bStatusIndex = ALL_SELF_SERVICE_STATUS.indexOf(b.status);
    if (aStatusIndex < bStatusIndex) {
      return 1;
    }
    if (aStatusIndex > bStatusIndex) {
      return -1;
    }

    // Sort by origin
    const originOrder = REVERSED_SELF_SERVICE_ORIGINS;
    const aOriginIndex = originOrder.indexOf(a.origin);
    const bOriginIndex = originOrder.indexOf(b.origin);
    if (aOriginIndex < bOriginIndex) {
      return -1;
    }
    if (aOriginIndex > bOriginIndex) {
      return 1;
    }

    return 0;
  });

/**
 * @param {import('modules/selfServices/types').SelfServiceData} selfService
 * @returns {Context}
 */
export const getSelfServiceUITagContext = (selfServices = []) => {
  const contexts = selfServices.reduce((acc, { type, originContext = {} }) => {
    const selfContexts = Object.entries(originContext).map(([origin, values]) => ({
      type,
      origin,
      status:
        // when in home, UI displays sent status instead of in progress, but on onsite, it displays nothing
        origin === SELF_SERVICE_ORIGIN.HOME && values.status === SELF_SERVICE_STATUS.IN_PROGRESS
          ? SELF_SERVICE_STATUS.SENT
          : values.status
    }));

    if (Object.keys(selfContexts).length > 0) {
      acc.push(...selfContexts);
    }
    return acc;
  }, []);

  const sortedContexts = sortByTypeOriginAndStatus(contexts);
  const contextToUse = sortedContexts.find(
    context => context.status !== SELF_SERVICE_STATUS.IN_PROGRESS && context.status !== SELF_SERVICE_STATUS.TO_BE_SENT
  );
  return contextToUse;
};

/**
 * @param {Pick<import('modules/selfServices/types').SelfServiceData, 'originContext'>} selfService
 * @returns {boolean} true if the self service has at least one answered context
 */
export const isSelfServiceAnswered = (selfService, skipOrigins = []) =>
  Object.entries(selfService?.originContext ?? {}).some(
    ([origin, { status }]) => status === SELF_SERVICE_STATUS.ANSWERED && !skipOrigins.includes(origin)
  );

/**
 * @param {Pick<import('modules/interventions/types/projections/kanban-intervention').KanbanIntervention, 'customerInfo' | 'vehicleInfo'>} partialIntervention
 * @return {boolean}
 */
export const canSendSelfService = ({ customerInfo = {}, vehicleInfo = {} }) => {
  const { email, phoneNumber } = customerInfo;
  const hasCompletedCustomerInfo = Boolean(phoneNumber?.local || email);
  const hasRegistration = Boolean(vehicleInfo.registration);

  return hasCompletedCustomerInfo && hasRegistration;
};

/**
 * @param {(string|Date)=} date
 * @param {[amount?: number, unit?: keyof import('date-fns').Duration]} boundary
 * @returns {boolean}
 */
const isOutOfBoundaryDate = (date, boundary = [2, 'weeks']) =>
  tryParse(date) && isAfter(startOfDay(date), add(endOfToday(), { [boundary[1]]: boundary[0] }));

const getSelfServiceDateError = (_, intervention) => {
  const { endDate } = intervention;
  return isOutOfBoundaryDate(endDate)
    ? {
      id: 'page.fiche.confirmation.selfService.disabled.date.outOfBoundary.endDate',
      defaultMessage: 'The end date of the file is more than two weeks away.',
      dataCy: 'error-endDate-out-of-boundary'
    }
    : undefined;
};

/**
 * @param {import('modules/selfServices/types').SelfService} selfService
 * @returns {boolean}
 */
export const isSelfServiceCheckinAnswered = selfService => {
  const documents = selfService?.mobility?.documents ?? [];

  const hasAnsweredHome = selfService?.originContext?.HOME?.status === SELF_SERVICE_STATUS.ANSWERED;
  const hasAnsweredDocuments =
    hasAnsweredHome &&
    documents.every(
      document => document.status === DOCUMENT_STATUS.ACCEPTED || document.status === DOCUMENT_STATUS.REFUSED
    );

  const isAnsweredAtDealership =
    selfService?.originContext?.KIOSK?.status === SELF_SERVICE_STATUS.ANSWERED ||
    selfService?.originContext?.ON_SITE?.status === SELF_SERVICE_STATUS.ANSWERED;

  return isAnsweredAtDealership || hasAnsweredDocuments;
};

export const disabledSelfServiceMessagesRules = {
  /**
   * @param {import('modules/selfServices/types').SelfServiceTypeEnum} selfServiceType
   * @param {InterventionWithDealer} intervention
   * @returns {{ values ?: Record<string, string | import('react-intl').MessageDescriptor> } & import('react-intl').MessageDescriptor}
   */
  GLOBAL: (selfServiceType, { selfServices = [], customerInfo, vehicleInfo }) => {
    // First step: Check the customer info
    if (!canSendSelfService({ customerInfo, vehicleInfo })) {
      return {
        id: 'page.fiche.form.selfService.errorTooltip',
        defaultMessage: 'Please fill vehicle registration and contact information',
        dataCy: 'error-vehicle-contact-info'
      };
    }

    // Second check: the next step (i.e check-out is the next step of check-in) should not be answered
    const nextStepType = SELF_SERVICE_TYPES[SELF_SERVICE_TYPES.indexOf(selfServiceType) + 1];
    const nextSelfService = selfServices.find(({ type }) => type === nextStepType);
    const nextS2Status = getSelfServiceStatus(nextSelfService);
    const hasNextSelfServiceNotSent = isSelfServiceStatusBelow(nextS2Status, SELF_SERVICE_STATUS.SENT);

    if (!hasNextSelfServiceNotSent) {
      const leaf = nextS2Status.toLowerCase();
      return {
        id: `page.fiche.confirmation.selfService.disabled.${nextStepType.toLowerCase()}.status.${leaf}`,
        defaultMessage: `The ${humanize(nextStepType)} is already ${humanize(nextS2Status)}.`,
        dataCy: `error-${nextStepType}-${nextS2Status}`
      };
    }
    return undefined;
  },
  /**
   * @param {InterventionWithDealer} intervention
   * @param {boolean} hasFinalInvoice
   * @returns {{ values ?: Record<string, string | import('react-intl').MessageDescriptor> } & import('react-intl').MessageDescriptor}
   */
  [SELF_SERVICE_TYPE.CHECK_IN]: ({ selfServices = [], startDate, dealer }, { hasFinalInvoice, locale } = {}) => {
    // Disabled if a final invoice exists
    if (hasFinalInvoice) {
      return {
        id: 'page.fiche.confirmation.selfService.disabled.finalInvoice',
        defaultMessage: 'A final invoice has been added to the file.',
        dataCy: 'error-final-invoice'
      };
    }

    const selfService =
      selfServices.find(({ type }) => type === SELF_SERVICE_TYPE.BREAKDOWN || type === SELF_SERVICE_TYPE.CHECK_IN) ??
      {};
    if (isSelfServiceCheckinAnswered(selfService)) {
      return {
        id: 'page.fiche.confirmation.selfService.disabled.check_in.status.answered',
        defaultMessage: 'The check-in is already answered.',
        dataCy: 'error-CHECK_IN-ANSWERED'
      };
    }

    const parsedStartDate = tryParse(startDate);
    const { checkInLinkValidityPeriod: validityPeriod } = dealer?.config?.selfService ?? {};
    if (validityPeriod && parsedStartDate) {
      const earliestStartDate = sub(parsedStartDate, parseISODuration(validityPeriod));

      const nowDate = convertTimeZones(new Date(), dealer.timeZone, UTC);

      if (isBefore(nowDate, earliestStartDate)) {
        return {
          id: 'page.fiche.confirmation.selfService.disabled.check_in.invalidPeriod',
          defaultMessage:
            'Impossible to send the check-in more than {period} before the appointment. Ask your dealer manager to change this parameter in the self-service settings.',
          dataCy: 'error-CHECK_IN-INVALID-PERIOD',
          values: {
            period: formatDuration(parseISODuration(validityPeriod), { locale })
          }
        };
      }
    }

    return undefined;
  },
  /**
   * @param {InterventionWithDealer} intervention
   * @returns {{ values ?: Record<string, string | import('react-intl').MessageDescriptor> } & import('react-intl').MessageDescriptor}
   */
  [SELF_SERVICE_TYPE.CHECK_OUT]: ({ endDate, startDate, selfServices = [], dealer }) => {
    const hasKiosk = dealer?.config?.selfService?.availableTypes?.[SELF_SERVICE_TYPE.CHECK_OUT].includes(SELF_SERVICE_ORIGIN.KIOSK);
    const selfService = selfServices.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_OUT);
    const isAnswered = !isSelfServiceStatusBelow(getSelfServiceStatus(selfService), SELF_SERVICE_STATUS.ANSWERED);
    const hasUnansweredKiosk = hasKiosk
      ? isSelfServiceStatusBelow(selfService?.originContext?.KIOSK?.status, SELF_SERVICE_STATUS.ANSWERED)
      : false;
    const isOnSiteCompleted = selfService?.originContext?.[SELF_SERVICE_ORIGIN.ON_SITE]?.status === SELF_SERVICE_STATUS.ANSWERED;
    const isManuallyCompleted = selfService?.originContext?.[SELF_SERVICE_ORIGIN.MANUAL]?.status === SELF_SERVICE_STATUS.ANSWERED;

    if (isAnswered && (!hasUnansweredKiosk || isOnSiteCompleted || isManuallyCompleted)) {
      return {
        id: 'page.fiche.confirmation.selfService.disabled.check_out.status.answered',
        defaultMessage: 'The check-out is already answered.',
        dataCy: 'error-CHECK_OUT-ANSWERED'
      };
    }

    return getSelfServiceDateError(SELF_SERVICE_TYPE.CHECK_OUT, { endDate: endDate === '' ? startDate : endDate });
  }
};

/**
 * @typedef {{
 *  hasFinalInvoice?: boolean,
 *  locale?: import("date-fns").Locale,
 *  intl:
 * }} Options
 */

/**
 * @param {import('modules/selfServices/types').SelfServiceTypeEnum} selfServiceType
 * @param {InterventionWithDealer} intervention
 * @param {Options} options
 * @return {boolean}
 */
export const getDisabledSendSelfServiceMessage = (selfServiceType, intervention, options) =>
  disabledSelfServiceMessagesRules.GLOBAL(selfServiceType, intervention) ??
  disabledSelfServiceMessagesRules[selfServiceType]?.(intervention, options);

/**
 * @param {import('modules/selfServices/types').SelfServiceTypeEnum} selfServiceType
 * @param {InterventionWithDealer} intervention
 * @param {Options} options
 * @return {boolean}
 */
export const canSendSelfServiceType = (selfServiceType, intervention, options) =>
  getDisabledSendSelfServiceMessage(selfServiceType, intervention, options) === undefined;

export const getForeignInterventionType = (entity = {}) =>
  FOREIGN_ENTITY_TYPES.find(type => Boolean(entity[`${type}Id`]));

/**
 * This function is used to abstract the logic to retrieve the foreign entity id of any class
 */
export const getForeignInterventionId = (entity = {}) => {
  const foreignEntityType = getForeignInterventionType(entity);
  const foreignEntityId = `${foreignEntityType}Id`;
  return foreignEntityType ? entity[foreignEntityId] : undefined;
};

const getSelfServicesNotToBeSent = selfServices =>
  selfServices?.reduce((acc, service) => {
    const isSent = Object.values(service.originContext ?? {}).some(
      ({ status }) => status !== SELF_SERVICE_STATUS.TO_BE_SENT
    );

    if (isSent) {
      const fn = service.type === SELF_SERVICE_TYPE.CHECK_OUT ? 'unshift' : 'push';
      acc[fn](service);
    }

    return acc;
  }, []) ?? [];

export const getSelfServiceTagContextUtils = (selfServices, hasSelfService) => {
  const [selfService] = getSelfServicesNotToBeSent(selfServices);
  return hasSelfService && selfService ? getSelfServiceUITagContext(selfServices) : undefined;
};

/**
 * @param {import("modules/global/generic.types").Action<AxiosError<unknown, unknown>>} action
 * @returns {import("react-intl").MessageDescriptor}
 */
export function getMediariesExportErrorNotification({ payload }) {
  const [code] = Object.entries(ERROR_MATCHERS).find(([, matcher]) => matcher(payload?.error?.response?.data)) ?? [];
  return ERROR_NOTIFICATION[code];
}
